import type { CanvasLayout } from "./content-types"
import api from "./api"
import type { CompanyTemplateRef } from "./company-templates"

// === Types ===

/**
 * BELGE TÜRÜ — kontrol listesi tablosu iki tür taşıyor: doldurulan kontrol
 * listesi ve gövdesi bölüm ağacı olan yazılı doküman. Doküman, kontrol
 * listesinin yaşam döngüsünü (revizyon, onay, antet, klasör) aynen paylaşıyor.
 */
export type ChecklistKind = "CHECKLIST" | "DOCUMENT"

export interface Checklist {
  id: string
  /** Yoksa eski kayıt = kontrol listesi. */
  kind?: ChecklistKind
  code: string
  description: string | null
  documentCode: string | null
  /** Yürürlük tarihi — ilk yayında atanır, sonradan düzeltilebilir. */
  effectiveDate: string | null
  revisionNo: string | null
  revisionDate: string | null
  hasItemCode: boolean
  hasEkpiZA: boolean
  hasVerification: boolean
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  // Şablon işareti + sahiplik (tenant)
  isSystemTemplate?: boolean
  /** ARŞİV: false = pasif; listelerde gizlenir, silinmez. */
  isActive?: boolean
  companyId?: string | null
  // Revizyon zinciri: aynı belgenin tüm revizyonları ortak aile kimliği taşır.
  // Yalnız güncel revizyon düzenlenebilir; eskiler salt-okunur arşivdir.
  revisionFamilyId?: string
  revisionSeq?: number
  isCurrentRevision?: boolean
  /** Onay sürecindeki durum (taslak / onayda / yayında / iptal). */
  revisionStatus?: ChecklistRevisionStatus
  /**
   * Gözden geçirme periyodu — AY cinsinden. Süreç kapanınca sonraki gözden
   * geçirme tarihi buradan hesaplanıyor. null = periyot tanımsız.
   */
  reviewPeriodMonths?: number | null
}

/** Revizyon geçmişi satırı (`GET /checklists/:id/revisions`). */
export interface ChecklistRevision {
  id: string
  code: string
  description: string | null
  revisionNo: string | null
  revisionDate: string | null
  revisionSeq: number
  isCurrentRevision: boolean
  revisionStatus: ChecklistRevisionStatus
  publishedAt: string | null
  /** Yürürlük tarihi — ilk yayında atanır, sonradan düzeltilebilir. */
  effectiveDate: string | null
  /** Revizyonun başlatıldığı an ve onu başlatan kullanıcı. */
  revisionStartedAt: string | null
  revisionStartedByUserId: string | null
  /** Gösterilecek ad (personel adı, yoksa e-posta). Sunucu çözer. */
  revisionStartedByName?: string | null
  /** Zengin metin (HTML) — neden revize edildi, ne değişti. */
  revisionNote: string | null
  createdAt: string
  updatedAt: string
  _count: { categories: number; items: number }
}

export interface ChecklistSummary extends Checklist {
  _count: {
    categories: number
    items: number
    sessions: number
  }
  scopeId?: string | null
  scope?: { id: string; title: string; parentId: string | null } | null
  subScopes?: { scope: { id: string; title: string; parentId: string | null } }[]
  documentFolderId?: string | null
  /** Liste bölümü (Kontrol Listeleri / Dokümanlar ekranındaki klasör). */
  groupId?: string | null
  sortOrder?: number
  columnOrder?: string[]
  // Şablon ise: paylaşıldığı firma id'leri.
  shareCompanyIds?: string[]
}

export interface ChecklistCategory {
  id: string
  name: string
  sortOrder: number
  checklistId: string
  items: ChecklistItem[]
}

export interface ChecklistItem {
  id: string
  code: string
  title: string
  description: string | null
  notes: string | null
  instruction: string | null
  sortOrder: number
  verificationMethod: string | null
  verificationMethodDescription: string | null
  ekpiZA: string | null
  ekpiZADescription: string | null
  categoryId: string
  checklistId: string
  ncTemplates: NcTemplate[]
  dataFields: DataRecordField[]
  itemDocuments?: ItemDocument[]
  instructionMedia?: InstructionMedia[]
  dataMedia?: InstructionMedia[]
}

export interface InstructionMedia {
  id: string
  fileId: string
  mediaType: "image" | "video"
  sortOrder: number
  url?: string | null
  file?: { id: string; filename: string; mimeType: string } | null
}

export interface NcTemplate {
  id: string
  text: string
  checklistItemId: string
}

export type DataFieldOperator = ">" | ">=" | "<" | "<=" | "=" | "!="

export interface DataRecordField {
  id: string
  code: string // A-Z
  name: string
  unit: string | null
  description: string | null
  formula: string | null
  operator: DataFieldOperator | null
  compareValue: string | null
  sortOrder?: number
  checklistItemId: string
}

export interface ChecklistWithDetails extends Checklist {
  categories: ChecklistCategory[]
  scopeId?: string | null
  scope?: { id: string; title: string; parentId: string | null } | null
  subScopes?: { scope: { id: string; title: string; parentId: string | null } }[]
  /** Dokümanlarla ortak taksonomi (DocumentFolder ağacı). */
  documentFolderId?: string | null
  documentFolder?: { id: string; name: string; parentId: string | null } | null
  /** Önizleme/yazdırma anteti — belge çekilirken birlikte geliyor. */
  companyTemplateId?: string | null
  companyTemplate?: CompanyTemplateRef | null
  /**
   * ANTET GRUBU — antetin toplu yönetimi ("Dikey 4x4" gibi). Grup seçiliyse
   * antet GRUPTAN gelir ve `companyTemplate` yok sayılır; grubun anteti
   * değişince bu liste de kendiliğinden değişir. Formlardaki alanın aynısı.
   */
  templateGroupId?: string | null
  templateGroup?: {
    id: string
    name: string
    template: CompanyTemplateRef | null
  } | null
  /**
   * KAPAK — liste basılırken önüne konan sayfa(lar); bir içerik tipi
   * (kind = COVER). Alanları basıldığı faaliyet dosyasının verisinden dolar.
   */
  coverId?: string | null
  cover?: {
    id: string
    key: string
    label: string
    approvalStatus?: string
    /** Kapak yerleşimi — detay yanıtında geliyor (önizleme ve Word çıktısı). */
    layoutJson?: CanvasLayout | null
  } | null
  /**
   * ARKA KAPAK — listenin SONUNA basılan kapak. Önizlemede tespitler buna
   * basılır; tespit yoksa "Tespit bulunamamıştır" yazar.
   */
  backCoverId?: string | null
  backCover?: {
    id: string
    key: string
    label: string
    approvalStatus?: string
  } | null
  /**
   * TESPİT FORMU — uygunsuzlukların basılacağı belge (bir FORM içerik tipi).
   *
   * Form bir ÇERÇEVE: anteti ve içerik alanı sayfayı kuruyor, tespit raporu
   * onun içine basılıyor. Bu yüzden formun KENDİ anteti de geliyor; yürürlükteki
   * antet `etkinAntet` ile çözülüyor (grup → belge).
   */
  findingsFormId?: string | null
  findingsForm?: {
    id: string
    key: string
    label: string
    approvalStatus?: string
    companyTemplate?: CompanyTemplateRef | null
    templateGroup?: { id: string; name: string; template: CompanyTemplateRef | null } | null
  } | null
  /** Madde tablosu kolon sırası; boş dizi = varsayılan. */
  columnOrder?: string[]
  revisionStartedAt?: string | null
  revisionStartedByUserId?: string | null
  /** Gösterilecek ad (personel adı, yoksa e-posta). Sunucu çözer. */
  revisionStartedByName?: string | null
  /** HAZIRLAYAN — belgeyi hazır sayan tek kişi; onaya yalnız o gönderebilir. */
  preparedByUserId?: string | null
  preparedByName?: string | null
  revisionNote?: string | null
}

export interface PaginatedChecklists {
  data: ChecklistSummary[]
  total: number
  page: number
  limit: number
  totalPages: number
}

// === Params ===
export interface ChecklistParams {
  page?: number
  limit?: number
  search?: string
  /** true → her revizyon ailesinden yalnız güncel revizyon döner. */
  onlyCurrent?: boolean
  /** Verilmezse sunucu yalnız kontrol listesi döndürür. */
  kind?: ChecklistKind
}

// === Payloads ===
export interface CreateChecklistPayload {
  /** Yalnız oluştururken; sonradan değiştirilemez. */
  kind?: ChecklistKind
  code: string
  description?: string
  documentCode?: string
  effectiveDate?: string
  revisionNo?: string
  revisionDate?: string
  hasItemCode?: boolean
  hasEkpiZA?: boolean
  hasVerification?: boolean
  scopeId?: string | null
  documentFolderId?: string | null
  companyTemplateId?: string | null
  columnOrder?: string[]
}

export interface UpdateChecklistPayload {
  code?: string
  description?: string
  documentCode?: string
  effectiveDate?: string
  revisionNo?: string
  revisionDate?: string
  hasItemCode?: boolean
  hasEkpiZA?: boolean
  hasVerification?: boolean
  scopeId?: string | null
  documentFolderId?: string | null
  /** Antet şablonu; null = şablonsuz (klasik) görünüm. */
  companyTemplateId?: string | null
  /** Antet grubu; null = gruba bağlı değil. Grup seçiliyse antet gruptan gelir. */
  templateGroupId?: string | null
  /** Kapak (içerik tipi, kind = COVER); null = kapaksız. */
  coverId?: string | null
  /** Arka kapak (kind = COVER); null = arka kapaksız. */
  backCoverId?: string | null
  /**
   * Gözden geçirme periyodu — AY. 1/3/6/12 hazır seçenek ama serbest sayı:
   * "15 ay" da girilebiliyor. null = periyot yok, sonraki tarih elle giriliyor.
   */
  reviewPeriodMonths?: number | null
  columnOrder?: string[]
  revisionNote?: string
}

export interface DuplicateChecklistPayload {
  code?: string
}

export interface CreateCategoryPayload {
  name: string
  sortOrder?: number
}

export interface UpdateCategoryPayload {
  name?: string
}

export interface ReorderPayload {
  orderedIds: string[]
}

export interface CreateItemPayload {
  code: string
  title: string
  description?: string
  notes?: string
  instruction?: string
  verificationMethod?: string
  verificationMethodDescription?: string
  ekpiZA?: string
  ekpiZADescription?: string
  sortOrder?: number
}

export interface UpdateItemPayload {
  code?: string
  title?: string
  description?: string
  notes?: string
  instruction?: string
  verificationMethod?: string
  verificationMethodDescription?: string
  ekpiZA?: string
  ekpiZADescription?: string
  categoryId?: string
}

export interface ReplaceNcTemplatesPayload {
  templates: string[]
}

export interface CreateDataFieldPayload {
  code: string
  name: string
  unit?: string
  description?: string
  formula?: string
  operator?: DataFieldOperator
  compareValue?: string
}

export interface UpdateDataFieldPayload {
  name?: string
  // null gönderilirse alan temizlenir. undefined → değiştirilmez.
  unit?: string | null
  description?: string | null
  formula?: string | null
  operator?: DataFieldOperator | null
  compareValue?: string | null
}

export const DATA_FIELD_OPERATORS: { value: DataFieldOperator; label: string }[] = [
  { value: ">", label: "Büyük (>)" },
  { value: ">=", label: "Büyük eşit (≥)" },
  { value: "<", label: "Küçük (<)" },
  { value: "<=", label: "Küçük eşit (≤)" },
  { value: "=", label: "Eşit (=)" },
  { value: "!=", label: "Eşit değil (≠)" },
]

/**
 * Girilen değeri, alanın operator + compareValue kuralına göre kontrol eder.
 * true = uygun, false = uygun değil (uyarı), null = kural yok / sayısal değil.
 */
export function checkDataFieldValue(
  value: string | number | null | undefined,
  operator: DataFieldOperator | null | undefined,
  compareValue: string | null | undefined,
): boolean | null {
  if (!operator || compareValue == null || compareValue === "") return null
  const v =
    typeof value === "number"
      ? value
      : parseFloat(String(value ?? "").replace(",", "."))
  const c = parseFloat(String(compareValue).replace(",", "."))
  if (Number.isNaN(v) || Number.isNaN(c)) return null
  switch (operator) {
    case ">": return v > c
    case ">=": return v >= c
    case "<": return v < c
    case "<=": return v <= c
    case "=": return v === c
    case "!=": return v !== c
    default: return null
  }
}

// === API Functions ===
export async function fetchChecklists(params: ChecklistParams = {}) {
  const { data } = await api.get<PaginatedChecklists>("/checklists", { params })
  return data
}

/** API tek istekte en fazla bunu döner (backend PaginationQueryDto sınırı). */
const MAX_PAGE_SIZE = 100

/**
 * Tüm listeleri sayfalayarak çeker. Tarayıcı ekranı gruplamaları ve sayaçları
 * tam veri üzerinden kurduğu için tek sayfayla yetinemez; 100'den fazla liste
 * olduğunda sessizce kırpılmasın diye kalan sayfalar da alınır.
 */
export async function fetchAllChecklists(
  params: Omit<ChecklistParams, "page" | "limit"> = {},
): Promise<ChecklistSummary[]> {
  const first = await fetchChecklists({ ...params, page: 1, limit: MAX_PAGE_SIZE })
  const pageCount = Math.ceil((first.total ?? first.data.length) / MAX_PAGE_SIZE)
  if (pageCount <= 1) return first.data

  const rest = await Promise.all(
    Array.from({ length: pageCount - 1 }, (_, i) =>
      fetchChecklists({ ...params, page: i + 2, limit: MAX_PAGE_SIZE }),
    ),
  )
  return [...first.data, ...rest.flatMap((r) => r.data)]
}

export async function fetchChecklist(id: string) {
  const { data } = await api.get<ChecklistWithDetails>(`/checklists/${id}`)
  return data
}

export async function createChecklist(payload: CreateChecklistPayload) {
  const { data } = await api.post<Checklist>("/checklists", payload)
  return data
}

export async function updateChecklist(id: string, payload: UpdateChecklistPayload) {
  const { data } = await api.patch<Checklist>(`/checklists/${id}`, payload)
  return data
}

export async function deleteChecklist(id: string) {
  await api.delete(`/checklists/${id}`)
}

export async function duplicateChecklist(id: string, payload?: DuplicateChecklistPayload) {
  const { data } = await api.post<Checklist>(`/checklists/${id}/duplicate`, payload ?? {})
  return data
}

// === Revizyonlar ===

export interface CreateRevisionPayload {
  code?: string
  revisionNo?: string
  revisionDate?: string
}

/**
 * Listeyi yeni revizyona çıkarır: içerik kopyalanır, kopya güncel revizyon olur,
 * önceki revizyon salt-okunur arşive düşer.
 */
export async function createChecklistRevision(id: string, payload: CreateRevisionPayload = {}) {
  const { data } = await api.post<Checklist>(`/checklists/${id}/revisions`, payload)
  return data
}

/** Listenin ait olduğu ailedeki tüm revizyonlar (yeniden eskiye). */
export async function fetchChecklistRevisions(id: string) {
  const { data } = await api.get<ChecklistRevision[]>(`/checklists/${id}/revisions`)
  return data
}

// === Şablonlar + Paylaşım ===

// Bir listeden şablon üret (kopya). Orijinal kalır.
export async function templatizeChecklist(id: string) {
  const { data } = await api.post<Checklist>(`/checklists/${id}/templatize`)
  return data
}

// Bir şablonu "kullan": kendi düzenlenebilir kopyanı al.
export async function copyChecklistTemplate(id: string) {
  const { data } = await api.post<Checklist>(`/checklists/${id}/use`)
  return data
}

// Şablonun paylaşıldığı firma id'leri.
export async function getChecklistShares(id: string) {
  const { data } = await api.get<string[]>(`/checklists/${id}/shares`)
  return data
}

// Şablonun paylaşıldığı firma kümesini değiştir.
export async function setChecklistShares(id: string, companyIds: string[]) {
  const { data } = await api.put<string[]>(`/checklists/${id}/shares`, { companyIds })
  return data
}

// Categories
export async function createCategory(checklistId: string, payload: CreateCategoryPayload) {
  const { data } = await api.post<ChecklistCategory>(
    `/checklists/${checklistId}/categories`, payload
  )
  return data
}

export async function updateCategory(checklistId: string, categoryId: string, payload: UpdateCategoryPayload) {
  const { data } = await api.patch<ChecklistCategory>(
    `/checklists/${checklistId}/categories/${categoryId}`, payload
  )
  return data
}

export async function deleteCategory(checklistId: string, categoryId: string) {
  await api.delete(`/checklists/${checklistId}/categories/${categoryId}`)
}

export async function reorderChecklists(payload: ReorderPayload) {
  await api.patch(`/checklists/reorder`, payload)
}

export async function reorderCategories(checklistId: string, payload: ReorderPayload) {
  await api.patch(`/checklists/${checklistId}/categories/reorder`, payload)
}

// Items
export async function createItem(checklistId: string, categoryId: string, payload: CreateItemPayload) {
  const { data } = await api.post<ChecklistItem>(
    `/checklists/${checklistId}/categories/${categoryId}/items`, payload
  )
  return data
}

export async function updateItem(checklistId: string, itemId: string, payload: UpdateItemPayload) {
  const { data } = await api.patch<ChecklistItem>(
    `/checklists/${checklistId}/items/${itemId}`, payload
  )
  return data
}

export async function deleteItem(checklistId: string, itemId: string) {
  await api.delete(`/checklists/${checklistId}/items/${itemId}`)
}

export async function reorderItems(checklistId: string, categoryId: string, payload: ReorderPayload) {
  await api.patch(`/checklists/${checklistId}/categories/${categoryId}/items/reorder`, payload)
}

// NC Templates
export async function replaceNcTemplates(checklistId: string, itemId: string, payload: ReplaceNcTemplatesPayload) {
  const { data } = await api.put<NcTemplate[]>(
    `/checklists/${checklistId}/items/${itemId}/nc-templates`, payload
  )
  return data
}

// Data Fields
export async function createDataField(checklistId: string, itemId: string, payload: CreateDataFieldPayload) {
  const { data } = await api.post<DataRecordField>(
    `/checklists/${checklistId}/items/${itemId}/data-fields`, payload
  )
  return data
}

export async function updateDataField(checklistId: string, itemId: string, fieldId: string, payload: UpdateDataFieldPayload) {
  const { data } = await api.patch<DataRecordField>(
    `/checklists/${checklistId}/items/${itemId}/data-fields/${fieldId}`, payload
  )
  return data
}

export async function deleteDataField(checklistId: string, itemId: string, fieldId: string) {
  await api.delete(`/checklists/${checklistId}/items/${itemId}/data-fields/${fieldId}`)
}

export async function reorderDataFields(checklistId: string, itemId: string, payload: ReorderPayload) {
  await api.patch(`/checklists/${checklistId}/items/${itemId}/data-fields/reorder`, payload)
}

// === Çöp Kutusu (silinmiş kontrol listeleri) ===
export interface TrashedChecklist extends Checklist {
  _count?: { categories: number }
}

export async function getTrashedChecklists() {
  const { data } = await api.get<TrashedChecklist[]>(`/checklists/trash/list`)
  return data
}

export async function restoreChecklist(id: string) {
  await api.post(`/checklists/${id}/restore`)
}

export async function permanentDeleteChecklist(id: string) {
  await api.delete(`/checklists/${id}/permanent`)
}

// Checklist Scopes
export async function assignChecklistScopes(checklistId: string, scopeIds: string[]) {
  const { data } = await api.post(`/checklists/${checklistId}/scopes`, { scopeIds })
  return data
}

export async function removeChecklistScope(checklistId: string, scopeId: string) {
  await api.delete(`/checklists/${checklistId}/scopes/${scopeId}`)
}

// Item Documents
export interface ItemDocument {
  id: string
  title: string
  type: string
  documentId: string | null
  fileId: string | null
  sortOrder: number
  file?: { id: string; filename: string; path: string; mimeType: string } | null
}

export async function getItemDocuments(checklistId: string, itemId: string): Promise<ItemDocument[]> {
  const { data } = await api.get<ItemDocument[]>(`/checklists/${checklistId}/items/${itemId}/documents`)
  return data
}

export async function replaceItemDocuments(
  checklistId: string,
  itemId: string,
  documents: { title: string; type: string; documentId?: string; fileId?: string; sortOrder?: number }[],
): Promise<ItemDocument[]> {
  const { data } = await api.put<ItemDocument[]>(`/checklists/${checklistId}/items/${itemId}/documents`, { documents })
  return data
}

export async function getInstructionMedia(checklistId: string, itemId: string): Promise<InstructionMedia[]> {
  const { data } = await api.get<InstructionMedia[]>(`/checklists/${checklistId}/items/${itemId}/instruction-media`)
  return data
}

export async function replaceInstructionMedia(
  checklistId: string,
  itemId: string,
  media: { fileId: string; mediaType?: string; sortOrder?: number }[],
): Promise<InstructionMedia[]> {
  const { data } = await api.put<InstructionMedia[]>(`/checklists/${checklistId}/items/${itemId}/instruction-media`, { media })
  return data
}

export async function getDataMedia(checklistId: string, itemId: string): Promise<InstructionMedia[]> {
  const { data } = await api.get<InstructionMedia[]>(`/checklists/${checklistId}/items/${itemId}/data-media`)
  return data
}

export async function replaceDataMedia(
  checklistId: string,
  itemId: string,
  media: { fileId: string; mediaType?: string; sortOrder?: number }[],
): Promise<InstructionMedia[]> {
  const { data } = await api.put<InstructionMedia[]>(`/checklists/${checklistId}/items/${itemId}/data-media`, { media })
  return data
}

// === Liste notları ve ekleri ===
//
// Madde düzeyindekilerden ayrı: notlar listenin/revizyonun kendisi hakkında,
// ekler ise listenin tamamına iliştirilen destekleyici dosyalar.

export interface ChecklistNote {
  id: string
  checklistId: string
  /** Zengin metin (HTML). */
  body: string
  sortOrder: number
  createdByUserId: string | null
  createdAt: string
  updatedAt: string
}

export interface ChecklistAttachment {
  id: string
  checklistId: string
  fileId: string
  title: string | null
  sortOrder: number
  createdAt: string
  file: { id: string; filename: string; mimeType: string; size: number }
}

export async function getChecklistNotes(id: string) {
  const { data } = await api.get<ChecklistNote[]>(`/checklists/${id}/notes`)
  return data
}

export async function createChecklistNote(id: string, body: string) {
  const { data } = await api.post<ChecklistNote>(`/checklists/${id}/notes`, { body })
  return data
}

export async function updateChecklistNote(id: string, noteId: string, body: string) {
  const { data } = await api.patch<ChecklistNote>(`/checklists/${id}/notes/${noteId}`, { body })
  return data
}

export async function deleteChecklistNote(id: string, noteId: string) {
  await api.delete(`/checklists/${id}/notes/${noteId}`)
}

// === Revizyon onay süreci ===

export type ChecklistRevisionStatus = "DRAFT" | "IN_REVIEW" | "PUBLISHED" | "CANCELLED"
export type ChecklistApprovalDecision = "PENDING" | "APPROVED" | "REJECTED"
export type ChecklistRevisionEventType =
  | "CREATED" | "SUBMITTED" | "REMINDED" | "APPROVED" | "REJECTED" | "PUBLISHED" | "CANCELLED"
  | "PREPARER_CHANGED"

/** Onay/olay satırlarında gösterilen kullanıcı özeti. */
export interface ApprovalUser {
  id: string
  email: string
  personnel: { firstName: string; lastName: string } | null
}

export interface ChecklistApproval {
  id: string
  userId: string
  decision: ChecklistApprovalDecision
  note: string | null
  decidedAt: string | null
  /** Bu kişiye en son ne zaman bildirim gitti. */
  notifiedAt: string | null
  sortOrder: number
  user: ApprovalUser
}

export interface ChecklistRevisionEvent {
  id: string
  type: ChecklistRevisionEventType
  note: string | null
  createdAt: string
  user: ApprovalUser | null
}

export interface ChecklistRevisionState {
  id: string
  revisionStatus: ChecklistRevisionStatus
  isCurrentRevision: boolean
  publishedAt: string | null
  publishedByUserId: string | null
  /** HAZIRLAYAN — belgeyi hazır sayan tek kişi; onaya yalnız o gönderebilir. */
  preparedByUserId: string | null
  preparedByName: string | null
  approvals: ChecklistApproval[]
  events: ChecklistRevisionEvent[]
}

/** Kullanıcının adı yoksa e-postaya düş — satır boş görünmesin. */
export function approvalUserLabel(user: ApprovalUser | null | undefined): string {
  if (!user) return "—"
  const name = [user.personnel?.firstName, user.personnel?.lastName].filter(Boolean).join(" ").trim()
  return name || user.email
}

/** Onaycı seçim listesindeki aday. */
export interface ApproverCandidate extends ApprovalUser {
  role: { name: string } | null
}

/**
 * Onaycı adayları — listenin kendi şirketindeki kullanıcılar.
 *
 * `/users` KULLANILMAZ: o uç yalnız super_admin/admin rolüne açık (company
 * kullanıcısında 403 → seçim ekranı boş kalıyordu) ve tüm firmaları döndürüyor.
 */
export async function getChecklistApproverCandidates(id: string) {
  const { data } = await api.get<ApproverCandidate[]>(`/checklists/${id}/approver-candidates`)
  return data
}

export async function getChecklistApprovalState(id: string) {
  const { data } = await api.get<ChecklistRevisionState>(`/checklists/${id}/approval`)
  return data
}

/** HAZIRLAYAN devri — onay sürecinde reddedilir (409). */
export async function setChecklistPreparer(id: string, userId: string) {
  const { data } = await api.put<{ preparedByUserId: string; preparedByName: string | null }>(
    `/checklists/${id}/preparer`,
    { userId },
  )
  return data
}

export async function setChecklistApprovers(id: string, userIds: string[]) {
  const { data } = await api.put<ChecklistApproval[]>(`/checklists/${id}/approvers`, { userIds })
  return data
}

/**
 * Tek onaycıyı listeden çıkarır — onay SÜRECİNDE de çalışır.
 *
 * `setChecklistApprovers` listeyi baştan yazdığı için süreçte kullanılamıyor
 * (verilmiş kararları silerdi); bu uç yalnız bir satır kaldırır.
 */
export async function removeChecklistApprover(id: string, userId: string) {
  const { data } = await api.delete<ChecklistApproval[]>(
    `/checklists/${id}/approvers/${userId}`,
  )
  return data
}

export async function submitChecklistForApproval(id: string, note?: string) {
  const { data } = await api.post<ChecklistRevisionState>(`/checklists/${id}/approval/submit`, { note })
  return data
}

export async function withdrawChecklistApproval(id: string, note?: string) {
  const { data } = await api.post<ChecklistRevisionState>(`/checklists/${id}/approval/withdraw`, { note })
  return data
}

export async function decideChecklistApproval(
  id: string,
  decision: "APPROVED" | "REJECTED",
  note?: string,
) {
  const { data } = await api.post<ChecklistRevisionState>(`/checklists/${id}/approval/decision`, { decision, note })
  return data
}

/** Bekleyen onaycılara hatırlatma bildirimi gönderir. */
export async function remindChecklistApprovers(id: string) {
  const { data } = await api.post<ChecklistRevisionState>(`/checklists/${id}/approval/remind`, {})
  return data
}

export async function publishChecklistRevision(id: string, note?: string) {
  const { data } = await api.post<ChecklistRevisionState>(`/checklists/${id}/approval/publish`, { note })
  return data
}

/**
 * Yayınlanmış revizyonu iptale alır.
 *
 * Yayınlanmış bir kontrol listesi SİLİNEMEZ (sahada kullanılmış olabilir,
 * denetim izi kopar); dolaşımdan çıkarmanın tek yolu bu.
 */
export async function revokeChecklistRevision(id: string, note?: string) {
  const { data } = await api.post<ChecklistRevisionState>(
    `/checklists/${id}/approval/revoke`,
    { note },
  )
  return data
}

/** Önizlemede görünen, henüz kaydedilmemiş doldurma verisi. */
export interface ChecklistPdfFill {
  results?: Record<string, "OK" | "NOK" | "NA">
  notes?: Record<string, string>
  fieldValues?: Record<string, Record<string, string>>
  manualNcs?: Record<string, { text: string; checked?: boolean }[]>
  ncSelections?: Record<string, string[]>
}

export interface ChecklistPdfParams {
  orientation?: "portrait" | "landscape"
  columns?: string[]
  showInstructions?: boolean
  showDataRecord?: boolean
  showResult?: boolean
  showFindings?: boolean
  showNotes?: boolean
  showImages?: boolean
  showDataEntry?: boolean
  fill?: ChecklistPdfFill
}

/**
 * Sunucuda üretilen PDF'i indirir ve tarayıcı içi bir object URL döndürür.
 *
 * POST kullanılır çünkü `fill` iç içe bir nesne ve sorgu dizesine sığmıyor;
 * uç veri değiştirmiyor, yalnız gövde taşıyor.
 *
 * Object URL, kullanıldıktan sonra `URL.revokeObjectURL` ile bırakılmalı —
 * aksi hâlde blob sekme kapanana kadar bellekte kalır.
 */
export async function fetchChecklistPdf(id: string, params: ChecklistPdfParams = {}) {
  const { data } = await api.post<Blob>(
    `/checklists/${id}/pdf`,
    { ...params, orientation: params.orientation ?? "portrait" },
    { responseType: "blob" },
  )
  return URL.createObjectURL(data)
}

/** Notları verilen sıraya göre yeniden dizer (dizideki konum = sortOrder). */
export async function reorderChecklistNotes(id: string, orderedIds: string[]) {
  await api.patch(`/checklists/${id}/notes/reorder`, { orderedIds })
}

export async function getChecklistAttachments(id: string) {
  const { data } = await api.get<ChecklistAttachment[]>(`/checklists/${id}/attachments`)
  return data
}

export async function addChecklistAttachment(id: string, fileId: string, title?: string) {
  const { data } = await api.post<ChecklistAttachment>(`/checklists/${id}/attachments`, { fileId, title })
  return data
}

export async function removeChecklistAttachment(id: string, attachmentId: string) {
  await api.delete(`/checklists/${id}/attachments/${attachmentId}`)
}

/**
 * `revisionSeq` → görünen revizyon no. Backend'deki `formatRevisionNo` ile
 * BİREBİR aynı kural (kalite dokümanı geleneği: ilk yayın "00").
 */
export function formatRevisionNo(seq: number): string {
  return String(Math.max(0, seq - 1)).padStart(2, "0")
}

/**
 * Bir revizyonun ekranda gösterilecek numarası.
 *
 * `revisionNo` kolonu bu kural yürürlüğe girmeden önce oluşmuş kayıtlarda boş;
 * o durumda sıra numarasından türetiyoruz — veriyi geriye dönük değiştirmeden
 * eski listeler de "00/01" olarak görünsün diye.
 */
export function displayRevisionNo(r: { revisionNo?: string | null; revisionSeq?: number }): string {
  return r.revisionNo ?? formatRevisionNo(r.revisionSeq ?? 1)
}

// ============================================================
// Kullanımdan kaldırma (onaylı) — AİLE düzeyinde
// ============================================================
//
// "Arşiv" adı terk edildi: arşiv kaydın saklanmasını çağrıştırıyor, buradaki
// karar belgenin DOLAŞIMDAN çıkarılması. Silme değil — kayıt yerinde kalır.

export type ChecklistWithdrawalStatus = "PENDING" | "APPROVED" | "REJECTED" | "CANCELLED"

export interface ChecklistWithdrawalApproval {
  id: string
  userId: string
  userName: string | null
  decision: ChecklistApprovalDecision
  note: string | null
  decidedAt: string | null
}

export interface ChecklistWithdrawal {
  id: string
  status: ChecklistWithdrawalStatus
  reason: string | null
  requestedByUserId: string | null
  requestedByName: string | null
  requestedAt: string
  decidedAt: string | null
  approvals: ChecklistWithdrawalApproval[]
}

export interface ChecklistWithdrawalState {
  /** Belge kullanımda mı (aile geneli). */
  isActive: boolean
  /** Karar bekleyen talep — yoksa null. */
  pending: ChecklistWithdrawal | null
  history: ChecklistWithdrawal[]
}

export async function getChecklistWithdrawal(id: string) {
  const { data } = await api.get<ChecklistWithdrawalState>(`/checklists/${id}/withdrawal`)
  return data
}

export async function requestChecklistWithdrawal(id: string, reason?: string) {
  const { data } = await api.post<ChecklistWithdrawalState>(
    `/checklists/${id}/withdrawal/request`,
    { reason },
  )
  return data
}

export async function decideChecklistWithdrawal(
  id: string,
  decision: "APPROVED" | "REJECTED",
  note?: string,
) {
  const { data } = await api.post<ChecklistWithdrawalState>(
    `/checklists/${id}/withdrawal/decision`,
    { decision, note },
  )
  return data
}

export async function cancelChecklistWithdrawal(id: string) {
  const { data } = await api.post<ChecklistWithdrawalState>(
    `/checklists/${id}/withdrawal/cancel`,
    {},
  )
  return data
}

export async function restoreChecklistFromWithdrawal(id: string) {
  const { data } = await api.post<ChecklistWithdrawalState>(
    `/checklists/${id}/withdrawal/restore`,
    {},
  )
  return data
}

// ============================================================
// Periyodik gözden geçirme
// ============================================================
//
// Revizyon DEĞİL: belge değişmez, "baktık, hâlâ geçerli" kaydı düşülür.
// 2024'te yayınlanan bir dokümanın 2025'te gözden geçirilmesi gibi.

export type ChecklistReviewOutcome = "NO_CHANGE" | "REVISION_NEEDED"

export const REVIEW_OUTCOME_LABEL: Record<ChecklistReviewOutcome, string> = {
  NO_CHANGE: "Değişiklik gerekmiyor",
  REVISION_NEEDED: "Revizyon gerekli",
}

/** Sürecin durumu — tek bir katılımcının kararı değil. */
export type ChecklistReviewStatus = "IN_PROGRESS" | "COMPLETED" | "CANCELLED"

export const REVIEW_STATUS_LABEL: Record<ChecklistReviewStatus, string> = {
  IN_PROGRESS: "Sürüyor",
  COMPLETED: "Tamamlandı",
  CANCELLED: "İptal",
}

/** Katılımcının kararı; karar vermeden önce PENDING. */
export type ChecklistReviewDecision = "PENDING" | ChecklistReviewOutcome

export const REVIEW_DECISION_LABEL: Record<ChecklistReviewDecision, string> = {
  PENDING: "Bekliyor",
  ...REVIEW_OUTCOME_LABEL,
}

export interface ChecklistReviewParticipant {
  id: string
  /** BİRİM — atama artık kişiye değil birime yapılıyor. */
  locationId: string | null
  locationName: string | null
  /** Birim içinden seçilen kişi; boşsa kararı birimden herkes verebilir. */
  personnelId: string | null
  personnelName: string | null
  /** Kararı FİİLEN veren kullanıcı — birim atamasında satırdan okunamıyor. */
  decidedByUserId: string | null
  decidedByName: string | null
  /** ESKİ kişi bazlı kayıtlar; yeni satırlarda boş. */
  userId: string | null
  userName: string | null
  decision: ChecklistReviewDecision
  note: string | null
  decidedAt: string | null
  notifiedAt: string | null
  sortOrder: number
}

/** Gözden geçirmede bir birim ataması; kişi opsiyonel. */
export interface ReviewUnitSelection {
  locationId: string
  personnelId?: string | null
}

/** Birim adayı — birimin AKTİF personeliyle birlikte. */
export interface ReviewUnitCandidate {
  id: string
  name: string
  personnel: { id: string; firstName: string; lastName: string; userId: string | null }[]
}

export interface ChecklistReview {
  id: string
  checklistId: string | null
  reviewedAt: string
  reviewedByUserId: string | null
  reviewedByName: string | null
  startedByUserId: string | null
  startedByName: string | null
  status: ChecklistReviewStatus
  /** Süreç sürerken null; kapanışta katılımcı kararlarından türetilir. */
  outcome: ChecklistReviewOutcome | null
  note: string | null
  dueAt: string | null
  nextReviewAt: string | null
  participants: ChecklistReviewParticipant[]
  createdAt: string
}

export interface ChecklistReviewState {
  reviews: ChecklistReview[]
  /**
   * Süren süreçte oturumdaki kullanıcının karar verebileceği satır — yoksa
   * null. Atama BİRİME yapıldığı için arayüz bunu satırlara bakarak
   * çözemiyor (kullanıcının hangi birimde olduğunu bilmiyor); sunucu söylüyor.
   */
  myParticipantId: string | null
  summary: {
    lastReviewedAt: string | null
    nextReviewAt: string | null
    /** Sonraki tarih geçmiş mi — hatırlatmanın dayanağı. */
    overdue: boolean
    /** Süren gözden geçirme varsa kimliği; aile başına en fazla bir tane. */
    activeReviewId: string | null
  }
}

export async function getChecklistReviews(id: string) {
  const { data } = await api.get<ChecklistReviewState>(`/checklists/${id}/reviews`)
  return data
}

export async function createChecklistReview(
  id: string,
  body: {
    reviewedAt?: string
    outcome: ChecklistReviewOutcome
    note?: string
    nextReviewAt?: string
  },
) {
  const { data } = await api.post<ChecklistReviewState>(`/checklists/${id}/reviews`, body)
  return data
}

/** Kişi seçimiyle süreci başlatır. */
/** Belgenin KALICI gözden geçirme birimi ayarı (süreç kaydı değil). */
export interface ChecklistReviewUnit {
  id: string
  locationId: string
  personnelId: string | null
  sortOrder: number
  location: { id: string; name: string } | null
  personnel: { id: string; firstName: string; lastName: string } | null
}

export async function getChecklistReviewUnits(id: string) {
  const { data } = await api.get<ChecklistReviewUnit[]>(`/checklists/${id}/review-units`)
  return data
}

/** Kalıcı birim ayarını baştan yazar. Boş liste = ayarı temizle. */
export async function setChecklistReviewUnits(id: string, units: ReviewUnitSelection[]) {
  const { data } = await api.put<ChecklistReviewUnit[]>(
    `/checklists/${id}/review-units`,
    { units },
  )
  return data
}

/** Gözden geçirmede seçilebilecek birimler (içindeki aktif personelle). */
export async function getChecklistReviewUnitCandidates(id: string) {
  const { data } = await api.get<ReviewUnitCandidate[]>(
    `/checklists/${id}/review-unit-candidates`,
  )
  return data
}

export async function startChecklistReview(
  id: string,
  body: {
    /** Verilmezse belgenin kalıcı birim ayarı kullanılır. */
    units?: ReviewUnitSelection[]
    dueAt?: string
    note?: string
    nextReviewAt?: string
  },
) {
  const { data } = await api.post<ChecklistReviewState>(
    `/checklists/${id}/reviews/start`,
    body,
  )
  return data
}

export async function setChecklistReviewParticipants(
  id: string,
  reviewId: string,
  units: ReviewUnitSelection[],
) {
  const { data } = await api.put<ChecklistReviewState>(
    `/checklists/${id}/reviews/${reviewId}/participants`,
    { units },
  )
  return data
}

export async function decideChecklistReview(
  id: string,
  reviewId: string,
  decision: ChecklistReviewOutcome,
  note?: string,
) {
  const { data } = await api.post<ChecklistReviewState>(
    `/checklists/${id}/reviews/${reviewId}/decision`,
    { decision, note },
  )
  return data
}

export async function remindChecklistReviewParticipants(id: string, reviewId: string) {
  const { data } = await api.post<ChecklistReviewState>(
    `/checklists/${id}/reviews/${reviewId}/remind`,
    {},
  )
  return data
}

export async function cancelChecklistReview(id: string, reviewId: string, note?: string) {
  const { data } = await api.post<ChecklistReviewState>(
    `/checklists/${id}/reviews/${reviewId}/cancel`,
    { note },
  )
  return data
}

export async function deleteChecklistReview(id: string, reviewId: string) {
  const { data } = await api.delete<ChecklistReviewState>(
    `/checklists/${id}/reviews/${reviewId}`,
  )
  return data
}

// === Ayarlar › Alana Bağla ===============================================
//
// Kontrol listesi, kullanıcının Tasarım › Alanlar'da tanımladığı SEÇENEKLİ
// alanlarla etiketleniyor (ör. "İlgili Standart"). Kod tarafında sabit bir
// liste yok — adaylar listenin kapsam ailesinden geliyor.

export interface BindingCandidateField {
  id: string
  key: string
  label: string
  fieldType: string
  options: unknown
}

export interface BindingCandidateGroup {
  id: string
  key: string
  label: string
  scope: { id: string; title: string } | null
  fields: BindingCandidateField[]
}

export interface ChecklistFieldBinding {
  id: string
  libraryFieldId: string
  values: string[]
  libraryField: {
    id: string
    key: string
    label: string
    fieldType: string
    options: unknown
    group: { id: string; key: string; label: string }
  }
}

export async function getBindingCandidates(checklistId: string) {
  const { data } = await api.get<BindingCandidateGroup[]>(
    `/checklists/${checklistId}/field-bindings/candidates`,
  )
  return data
}

export async function getChecklistFieldBindings(checklistId: string) {
  const { data } = await api.get<ChecklistFieldBinding[]>(
    `/checklists/${checklistId}/field-bindings`,
  )
  return data
}

/** Boş `values` bağı SİLER — "hiç seçili değil" ile "bağlı değil" aynı şey. */
export async function setChecklistFieldBinding(
  checklistId: string,
  libraryFieldId: string,
  values: string[],
) {
  const { data } = await api.put<ChecklistFieldBinding[]>(
    `/checklists/${checklistId}/field-bindings/${libraryFieldId}`,
    { values },
  )
  return data
}

export async function removeChecklistFieldBinding(checklistId: string, libraryFieldId: string) {
  const { data } = await api.delete<ChecklistFieldBinding[]>(
    `/checklists/${checklistId}/field-bindings/${libraryFieldId}`,
  )
  return data
}

/** `options` iki biçimde kayıtlı olabilir; ikisini de {value,label}'a indirger. */
export function bindingOptions(options: unknown): { value: string; label: string }[] {
  if (!Array.isArray(options)) return []
  const out: { value: string; label: string }[] = []
  for (const o of options) {
    if (typeof o === "string") out.push({ value: o, label: o })
    else if (o && typeof o === "object") {
      const v = (o as { value?: unknown }).value
      if (typeof v === "string") {
        out.push({ value: v, label: String((o as { label?: unknown }).label ?? v) })
      }
    }
  }
  return out
}
